import React from 'react';
import styled from 'styled-components';
import { OnboardingCard, FlexRow, FlexColumn, IconContainer, THEME } from './OnboardingLayoutComponents';
import { OnboardingButtonPrimary } from './OnboardingButton';
import { BodyShortLargeStrong, BodyShortMedium, LabelMedium } from './OnboardingTypography';

// Styled components using FlexRow instead of custom div
const CardHeader = styled(FlexRow)`
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${THEME.spacing.md};
`;

const CompanyName = styled(BodyShortLargeStrong)`
  margin: 0;
  color: ${THEME.colors.primaryDark};
`;

const StatusBadge = styled.div`
  padding: ${THEME.spacing.xs} ${THEME.spacing.sm};
  border-radius: ${THEME.borderRadius.sm};
  font-size: ${THEME.fontSize.sm};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${({ status }) => {
    switch (status) {
      case 'completed':
        return `
          background-color: ${THEME.colors.successBg};
          color: ${THEME.colors.success};
          border: 1px solid ${THEME.colors.successBorder};
        `;
      case 'in_progress':
        return `
          background-color: ${THEME.colors.warningBg};
          color: ${THEME.colors.warning};
          border: 1px solid ${THEME.colors.warningBorder};
        `;
      case 'pending':
      default:
        return `
          background-color: ${THEME.colors.infoBg};
          color: ${THEME.colors.info};
          border: 1px solid ${THEME.colors.infoBorder};
        `;
    }
  }}
`;

const InfoRow = styled(FlexRow)`
  align-items: center;
  gap: ${THEME.spacing.sm};
  margin-bottom: ${THEME.spacing.sm};
  font-size: ${THEME.fontSize.sm};
  color: ${THEME.colors.textSecondary};
`;

const InfoLabel = styled.span`
  font-family: Manrope;
  font-size: ${THEME.fontSize.sm};
  font-weight: 500;
  color: ${THEME.colors.textMuted};
  min-width: 80px;
`;

const InfoValue = styled.span`
  font-family: Manrope;
  font-size: ${THEME.fontSize.sm};
  color: ${THEME.colors.text};
`;

const ActionsContainer = styled(FlexRow)`
  justify-content: flex-end;
  gap: ${THEME.spacing.sm};
  margin-top: ${THEME.spacing.lg};
  padding-top: ${THEME.spacing.md};
  border-top: 1px solid ${THEME.colors.borderLight};
`;

/**
 * ClientOnboardingCard Component
 * 
 * Displays information about a client onboarding in a card format
 * 
 * @param {Object} props - Component props
 * @param {Object} props.onboarding - Onboarding object with client information
 * @param {Function} props.onContinue - Callback when continue button is clicked
 * @returns {JSX.Element} The client onboarding card component
 */
const ClientOnboardingCard = ({ onboarding, onContinue }) => {
  const getStatusDisplay = (onboarding) => {
    // Use isCompleted field to determine actual status
    if (onboarding.isCompleted === true) {
      return { text: 'Fullført', status: 'completed' };
    }
    
    // Check if onboarding has started (has some progress)
    if (onboarding.kosCreditorCreated || onboarding.hasSignedAgreement) {
      return { text: 'Pågår', status: 'in_progress' };
    }
    
    // Default to pending
    return { text: 'Venter', status: 'pending' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('no-NO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const statusDisplay = getStatusDisplay(onboarding);

  return (
    <OnboardingCard padding={THEME.spacing.xl}>
      <CardHeader>
        <CompanyName>{onboarding.companyName || 'Unnamed Company'}</CompanyName>
        <StatusBadge status={statusDisplay.status}>
          {statusDisplay.text}
        </StatusBadge>
      </CardHeader>

      <FlexColumn gap={THEME.spacing.xs}>
        <InfoRow>
          <InfoLabel>Org. No:</InfoLabel>
          <InfoValue>{onboarding.orgNo || 'N/A'}</InfoValue>
        </InfoRow>
        
        <InfoRow>
          <InfoLabel>Email:</InfoLabel>
          <InfoValue>{onboarding.email || 'N/A'}</InfoValue>
        </InfoRow>
        
        <InfoRow>
          <InfoLabel>Created:</InfoLabel>
          <InfoValue>{formatDate(onboarding.createdDate)}</InfoValue>
        </InfoRow>
        
        {onboarding.lastModified && (
          <InfoRow>
            <InfoLabel>Modified:</InfoLabel>
            <InfoValue>{formatDate(onboarding.lastModified)}</InfoValue>
          </InfoRow>
        )}
        
        {onboarding.clientSystem && (
          <InfoRow>
            <InfoLabel>System:</InfoLabel>
            <InfoValue>{onboarding.clientSystem}</InfoValue>
          </InfoRow>
        )}
      </FlexColumn>

      <ActionsContainer>
        <OnboardingButtonPrimary
          onClick={onContinue}
          size="sm"
        >
          <LabelMedium>Fortsett</LabelMedium>
        </OnboardingButtonPrimary>
      </ActionsContainer>
    </OnboardingCard>
  );
};

export default ClientOnboardingCard;